import * as SheetPrimitive from "@radix-ui/react-dialog";
import { cva, type VariantProps } from "class-variance-authority";
import { X } from "lucide-react";
import * as React from "react";

import { cn } from "@/lib/utils";

/**
 * Robust helper to check if an event originated from inside the Sonner toast container.
 * Handles: svg/path elements, Text nodes, composedPath, and direct element checks.
 */
function isInsideToastEvent(event: Event): boolean {
  const TOAST_SELECTORS = '[data-sonner-toaster], [data-sonner-toast]';
  
  // 1. Try composedPath first (most reliable for shadow DOM and event retargeting)
  const originalEvent = (event as CustomEvent)?.detail?.originalEvent;
  const path = originalEvent?.composedPath?.() ?? (event as PointerEvent).composedPath?.();
  if (path && path.length > 0) {
    for (const node of path) {
      if (node instanceof Element && node.matches(TOAST_SELECTORS)) {
        return true;
      }
    }
  }
  
  // 2. Get the actual target element
  let target: EventTarget | null = originalEvent?.target ?? event.target;
  
  // Handle non-Element targets (Text nodes, etc.)
  if (target && !(target instanceof Element)) {
    target = (target as Node).parentElement;
  }
  
  // 3. Check if target is inside toast using closest()
  if (target instanceof Element) {
    if (target.closest(TOAST_SELECTORS)) {
      return true;
    }
  }
  
  // 4. Final fallback: check if toast container contains the target
  const toastContainer = document.querySelector('[data-sonner-toaster]');
  if (toastContainer && target instanceof Node && toastContainer.contains(target)) {
    return true;
  }
  
  return false;
}

const Sheet = SheetPrimitive.Root;

const SheetTrigger = SheetPrimitive.Trigger;

const SheetClose = SheetPrimitive.Close;

const SheetPortal = SheetPrimitive.Portal;

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Overlay
    className={cn(
      "fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className,
    )}
    {...props}
    ref={ref}
  />
));
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName;

const sheetVariants = cva(
  "fixed z-50 gap-4 bg-background shadow-lg transition ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:duration-500",
  {
    variants: {
      side: {
        top: "inset-x-0 top-0 border-b p-6 data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
        bottom:
          "inset-x-0 bottom-0 top-0 border-t rounded-t-2xl data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom flex flex-col overflow-hidden",
        left: "inset-y-0 left-0 h-full w-3/4 border-r p-6 data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm",
        right:
          "inset-y-0 right-0 h-full w-3/4 border-l p-6 data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-sm",
      },
    },
    defaultVariants: {
      side: "right",
    },
  },
);

interface SheetContentProps
  extends React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content>,
    VariantProps<typeof sheetVariants> {}

const SheetContent = React.forwardRef<React.ElementRef<typeof SheetPrimitive.Content>, SheetContentProps>(
  ({ side = "right", className, children, onInteractOutside, onPointerDownOutside, onFocusOutside, ...props }, ref) => {
    // Wrap ALL dismiss-related handlers to ignore clicks on Sonner toasts
    const handleInteractOutside = React.useCallback(
      (event: Event) => {
        if (isInsideToastEvent(event)) {
          event.preventDefault();
          return;
        }
        if (onInteractOutside) {
          onInteractOutside(event as Parameters<typeof onInteractOutside>[0]);
        }
      },
      [onInteractOutside]
    );

    const handlePointerDownOutside = React.useCallback(
      (event: Event) => {
        if (isInsideToastEvent(event)) {
          event.preventDefault();
          return;
        }
        if (onPointerDownOutside) {
          onPointerDownOutside(event as Parameters<typeof onPointerDownOutside>[0]);
        }
      },
      [onPointerDownOutside]
    );

    const handleFocusOutside = React.useCallback(
      (event: Event) => {
        if (isInsideToastEvent(event)) {
          event.preventDefault();
          return;
        }
        if (onFocusOutside) {
          onFocusOutside(event as Parameters<typeof onFocusOutside>[0]);
        }
      },
      [onFocusOutside]
    );

    return (
      <SheetPortal>
        <SheetOverlay />
        <SheetPrimitive.Content
          ref={ref}
          className={cn(sheetVariants({ side }), className)}
          onInteractOutside={handleInteractOutside}
          onPointerDownOutside={handlePointerDownOutside}
          onFocusOutside={handleFocusOutside}
          {...props}
        >
          {children}
          <SheetPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity data-[state=open]:bg-secondary hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none">
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </SheetPrimitive.Close>
        </SheetPrimitive.Content>
      </SheetPortal>
    );
  },
);
SheetContent.displayName = SheetPrimitive.Content.displayName;

const SheetHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col space-y-2 text-center sm:text-left", className)} {...props} />
);
SheetHeader.displayName = "SheetHeader";

const SheetFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)} {...props} />
);
SheetFooter.displayName = "SheetFooter";

const SheetTitle = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Title ref={ref} className={cn("text-lg font-semibold text-foreground", className)} {...props} />
));
SheetTitle.displayName = SheetPrimitive.Title.displayName;

const SheetDescription = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Description ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />
));
SheetDescription.displayName = SheetPrimitive.Description.displayName;

export {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetOverlay,
  SheetPortal,
  SheetTitle,
  SheetTrigger,
};
